/**
 * ImportMetaButton - Button to open Meta ad browser modal
 *
 * Features:
 * - Shows only when team has active Meta connection
 * - Opens AdBrowserModal on click
 * - Triggers import via API when ads selected
 * - Shows progress toast on import start
 * - Tracks active import job for button state
 */

import { Download, Loader2 } from 'lucide-react';
import { useState } from 'react';

import { AdBrowserModal } from '@/components/meta';
import { Button } from '@/components/ui/Button';
import { useToast } from '@/components/ui/Toast';
import { useAuth } from '@/context/AuthContext';
import { useImportMutation, useImportJobStatus } from '@/hooks/useImportJob';
import { useMetaConnection } from '@/hooks/useMetaConnection';

interface ImportMetaButtonProps {
  projectId: string;
}

export function ImportMetaButton({ projectId }: ImportMetaButtonProps) {
  const { currentTeam } = useAuth();
  const teamId = currentTeam?.teamId ?? '';
  const toast = useToast();

  // Check if team has active Meta connection
  const { data: connection, isLoading: isLoadingConnection } = useMetaConnection(teamId);

  // Modal and import state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeJobId, setActiveJobId] = useState<string | null>(null);

  // Import mutation
  const importMutation = useImportMutation();

  // Poll for active import job status
  const { data: activeJob } = useImportJobStatus(
    projectId,
    activeJobId ?? undefined,
    {
      enabled: !!activeJobId,
      onComplete: (job) => {
        toast.success(
          'Import Complete',
          `${job.completedItems} video${job.completedItems !== 1 ? 's' : ''} imported successfully`
        );
        // Clear job and close modal after 2 seconds to let user see completion
        setTimeout(() => {
          setActiveJobId(null);
          setIsModalOpen(false);
        }, 2000);
      },
      onPartial: (job) => {
        toast.warning(
          'Import Partially Complete',
          `${job.completedItems} imported, ${job.failedItems} failed. Expand progress in the modal for details.`
        );
        // Keep activeJobId set so ImportProgress stays visible with per-item details
      },
      onFailed: (job) => {
        const failedCount = job.failedItems || 0;
        toast.error(
          'Import Failed',
          `${failedCount} item${failedCount !== 1 ? 's' : ''} failed. Expand progress in the modal for details.`
        );
        // Keep activeJobId set so ImportProgress stays visible in modal with per-item errors
      },
    }
  );

  // Don't show button if no team selected
  if (!teamId) {
    return null;
  }

  // Don't show button if no connection or loading
  if (isLoadingConnection) {
    return (
      <Button variant="secondary" disabled>
        <Loader2 className="h-4 w-4 animate-spin mr-2" />
        Checking Meta...
      </Button>
    );
  }

  // Hide button if no active connection
  if (!connection || connection.status !== 'active') {
    return null;
  }

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setActiveJobId(null);
  };

  const handleImport = async (adIds: string[]) => {
    importMutation.mutate(
      {
        projectId,
        adIds,
        autoAnalyze: true,
      },
      {
        onSuccess: (response) => {
          // Show toast
          toast.success(
            'Import Started',
            `Importing ${adIds.length} video${adIds.length !== 1 ? 's' : ''}...`
          );

          // Track job for polling - modal stays open to show progress
          setActiveJobId(response.jobId);
        },
        onError: (error) => {
          toast.error(
            'Import Failed',
            error instanceof Error ? error.message : 'Failed to start import'
          );
        },
      }
    );
  };

  // Only treat as "actively importing" when the mutation is pending or the job is still in progress
  const isJobActive = !!activeJob && (activeJob.status === 'queued' || activeJob.status === 'processing');
  const isImporting = importMutation.isPending || isJobActive;

  return (
    <>
      <Button
        variant="secondary"
        onClick={handleOpenModal}
        disabled={isImporting}
        leftIcon={
          isImporting ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Download className="h-4 w-4" />
          )
        }
      >
        {isImporting
          ? `Importing ${activeJob?.completedItems ?? 0}/${activeJob?.totalItems ?? '...'}`
          : 'Import from Meta'}
      </Button>

      <AdBrowserModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onImport={handleImport}
        teamId={teamId}
        projectId={projectId}
        isImporting={importMutation.isPending}
        activeJob={activeJob ?? null}
      />
    </>
  );
}
